import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";

import { Tag } from "primereact/tag";
import { Card } from "primereact/card";
import { Toast } from "primereact/toast";

import { Station } from "./interfaces";

import Loader from "../../components/Loader";

import { useFilters } from "./hooks/useFilters";
import { useStationData } from "./hooks/useStationData";

import { getStatusSeverity, StatusFilter } from "./components/StatusFilter";
import { ReservableFilter } from "./components/ReservableFilter";
import { ActionButtons } from "./components/ActionButtons";

const StationList: React.FC = () => {
  const toast = useRef<Toast>(null);
  const { stations, loading } = useStationData({ toast });
  const { filters, globalFilterValue, onGlobalFilterChange } = useFilters();

  const [selectedStation, setSelectedStation] = useState<Station | null>(null);
  const navigate = useNavigate();

  const header = (
    <span className="p-input-icon-left w-full sm:w-20rem flex-order-1 sm:flex-order-0">
      <i className="pi pi-search ml-3"></i>
      <InputText
        value={globalFilterValue}
        onChange={onGlobalFilterChange}
        placeholder={"Search"}
        className="pl-6 border rounded-md"
      />
    </span>
  );

  return (
    <>
      <Toast ref={toast} />
      <Card className="p-4" title={"Station Management"}>
        {loading ? (
          <Loader />
        ) : (
          <DataTable
            value={stations}
            paginator
            rows={10}
            dataKey="id"
            filters={filters}
            filterDisplay="menu"
            globalFilterFields={["id", "city", "address", "customerName"]}
            emptyMessage="No stations found."
            header={header}
            selectionMode="single"
            selection={selectedStation}
            onSelectionChange={(e) => setSelectedStation(e.value)}
            onRowSelect={(e) => navigate(`/station-details/${e.data.id}`)}
            className="p-datatable-gridlines"
            showGridlines
            responsiveLayout="scroll"
          >
            <Column
              field="id"
              header="Station ID"
              filter
              filterPlaceholder="Search by ID"
              style={{ minWidth: "12rem" }}
              sortable
            />
            <Column
              field="customerName"
              header="Customer Name"
              filter
              filterPlaceholder="Search by Customer"
              style={{ minWidth: "12rem" }}
              sortable
            />
            <Column
              field="city"
              header="City"
              filter
              filterPlaceholder="Search by City"
              style={{ minWidth: "12rem" }}
              sortable
            />
            <Column
              field="address"
              header="Street"
              filter
              filterPlaceholder="Search by Street"
              style={{ minWidth: "12rem" }}
              sortable
            />
            <Column
              field="status"
              header="Status"
              body={({ status }: Station) => (
                <Tag value={status} severity={getStatusSeverity(status)} />
              )}
              filter
              filterElement={StatusFilter}
              style={{ minWidth: "10rem" }}
              sortable
            />
            <Column
              field="isReservable"
              header="Reservable"
              body={({ isReservable }: Station) =>
                isReservable ? (
                  <Tag value="Yes" severity="success" />
                ) : (
                  <Tag value="No" severity="danger" />
                )
              }
              filter
              filterElement={ReservableFilter}
              style={{ minWidth: "10rem" }}
              sortable
            />
            <Column
              header="Actions"
              body={(station: Station) => (
                <ActionButtons stationId={station.id} />
              )}
              style={{ minWidth: "12rem" }}
            />
          </DataTable>
        )}
      </Card>
    </>
  );
};

export default StationList;
